import { useState, useEffect, useRef } from 'react';

import styles from './Login.module.scss';
import { KakaoIcon, DeleteIcon, NoViewPwIcon, ViewPwIcon } from 'assets';
import toast from 'react-hot-toast';

import { useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router';
import i18n from 'i18n';

import Btn from 'pages/Telemedicine/components/common/Btn/Btn';
import ErrorMsgBox from 'components/common/ErrorMsgBox';

import { useAppDispatch, RootState } from 'modules';
import {
  changeField,
  initField,
  checkEmail,
  initializeForm,
  login,
} from 'modules/account';
import * as nativeApi from 'utils/nativeApi';

import validateEmail from 'utils/validateEmail';
import logEvent from 'utils/logEvent';
import { setActiveLoginBottomSheet } from 'modules/account';

export default function Login({
  onCloseLoginBottomSheet,
}: {
  onCloseLoginBottomSheet: () => void;
}) {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [seletedLoginMethod, setSeletedLoginMethod] = useState<
    null | 'kakao' | 'email'
  >(null);
  const { t, i18n } = useTranslation(['account', 'utils']);

  const { form, emailDuplicated, error, ApiEmail, userId } = useSelector(
    (state: RootState) => ({
      form: state.account.login,
      emailDuplicated: state.account.register?.duplicated,
      error: state.account.error,
      ApiEmail: state.account.register?.with,
      userId: state.user.users?.[0].user_id,
    }),
  );

  const onKakaoLoginClick = () => {
    nativeApi.handleSocialLogin('kakao');
  };

  const usePrevious = (value: any) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  const prevEmail = usePrevious(form.email);

  // 이메일 중복인 상태에서(비밀번호 입력 단계) 이메일 입력값이 바뀌면 초기화
  useEffect(() => {
    if (emailDuplicated && prevEmail !== form.email) {
      dispatch(initField({ form: 'register', key: 'duplicated' }));
    }
  }, [emailDuplicated, prevEmail, form.email, dispatch]);

  // 등록된 이메일이 아니라면 회원가입 페이지로 이동
  useEffect(() => {
    if (emailDuplicated === false) {
      dispatch(
        changeField({
          form: 'register',
          key: 'email',
          value: form.email,
        }),
      );
      // history.push('/register/country');
      dispatch(setActiveLoginBottomSheet(false));
      history.push('/register/country');
      dispatch(initField({ form: 'register', key: 'duplicated' }));
    }
  }, [emailDuplicated, form.email, dispatch, history]);

  // 로그인 성공 후
  useEffect(() => {
    if (userId) {
      if (form.password) {
        logEvent({
          ga: {
            eventName: 'login',
            eventParam: { method: 'email' },
          },
        });
      } else {
        logEvent({
          ga: {
            eventName: 'login',
            eventParam: { method: 'kakao' },
          },
        });
      }
      dispatch(initializeForm('login'));
      onCloseLoginBottomSheet();
      toast.success(t('login.로그인에 성공하셨습니다'));
    }
  }, [userId, history, dispatch, form.password]);

  // 특정 Input field 변경 이벤트 핸들러
  const onChangeField = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    //name -> email.
    dispatch(
      changeField({
        form: 'login',
        key: name,
        value,
      }),
    );
  };

  // 특정 field 초기화
  // const onInitField = (e: React.MouseEvent<HTMLDivElement>) => {
  //   const type = e.currentTarget.getAttribute('data-type');
  //   dispatch(
  //     initField({
  //       form: 'login',
  //       key: type,
  //     }),
  //   );
  // };

  const onCheckEmail = () => {
    dispatch(checkEmail(form.email));
  };

  const onPasswordForgotClick = () => {
    dispatch(setActiveLoginBottomSheet(false));
    dispatch(initializeForm('forgot'));
    history.push('/forgot/email');
  };

  const onToggleChange = (e: React.MouseEvent<HTMLElement>) => {
    const type = e.currentTarget.getAttribute('data-type')!;
    dispatch(
      changeField({
        form: 'login',
        key: type,
        value: !form[type],
      }),
    );
  };

  // 폼 등록 이벤트 핸들러
  const onLoginSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const { email, password } = form;

    dispatch(login({ email: email, password: password.toString() }));
    localStorage.setItem('email', email);
  };

  const KAKAO = 'KAKAO';
  return (
    <section className={styles.loginContainer}>
      <h1>
        {seletedLoginMethod && emailDuplicated ? (
          <Trans
            t={t}
            i18nKey="login.welcomeTemp"
            components={{ br: <br /> }}
          />
        ) : (
          t('login.로그인 또는 회원가입을 해주세요')
        )}
      </h1>
      {!seletedLoginMethod && (
        <div>
          {i18n.language === 'ko' && (
            <div className={styles.kakaoLoginBtnWrap}>
              <Btn
                BtnIcon={KakaoIcon}
                bgColor="#FEE500"
                borderRadius="8px"
                fontColor="#000000"
                fontSize="16px"
                onClick={onKakaoLoginClick}
              >
                {t('login.kakaoLogin')}
              </Btn>
            </div>
          )}

          <div className={styles.emailLoginBtnWrap}>
            <Btn
              onClick={() => {
                setSeletedLoginMethod('email');
              }}
            >
              {t('login.startEmailLogin')}
            </Btn>
          </div>
        </div>
      )}
      {seletedLoginMethod === 'email' && (
        <div className={styles.inputWrapper} style={{ marginBottom: '20px' }}>
          <div className={styles.inputTitle}>
            {emailDuplicated ? t('login.이메일') : t('login.startEmailLogin')}
          </div>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={onChangeField}
            placeholder={t('login.enterEmailAddress')}
            className={styles.input}
          />
          {/* {form.email && (
            <div
              onClick={onInitField}
              data-type="email"
              className={styles.deleteIcon}
            >
              <DeleteIcon />
            </div>
          )} */}
        </div>
      )}

      {ApiEmail === KAKAO && emailDuplicated ? (
        <ErrorMsgBox visible={emailDuplicated}>
          {t('login.KAKAO_ACCOUNT_WITH_EMAIL_ALREADY_EXIST')}
        </ErrorMsgBox>
      ) : (
        emailDuplicated &&
        seletedLoginMethod === 'email' && (
          <>
            <div className={styles.inputWrapper}>
              <div className={styles.inputTitle}>{t('login.비밀번호')}</div>

              <input
                type={form.passwordShow ? 'text' : 'password'}
                name="password"
                value={form.password}
                onChange={onChangeField}
                placeholder={t('login.password')}
                className={styles.input}
              />
              <div
                onClick={onToggleChange}
                data-type="passwordShow"
                className={styles.viewPwIcon}
              >
                {form.passwordShow ? <ViewPwIcon /> : <NoViewPwIcon />}
              </div>
            </div>
            <ErrorMsgBox visible={error}>
              {error?.code === 'AUTH_FAILED'
                ? t('login.AUTH_FAILED')
                : t('login.UNKNOWN')}
            </ErrorMsgBox>
            <Btn
              disabled={!validateEmail(form.email) || form.password.length < 6}
              onClick={onLoginSubmit}
            >
              {t('login.login')}
            </Btn>
            <div
              className={styles.findPassword}
              onClick={onPasswordForgotClick}
            >
              {t('login.forgotPassword')}
            </div>
          </>
        )
      )}
      {emailDuplicated === null && seletedLoginMethod === 'email' && (
        <>
          <Btn
            onClick={onCheckEmail}
            disabled={!validateEmail(form.email)}
            margin={'40px 0 0 0'}
          >
            {t('login.로그인 / 회원가입')}
          </Btn>
          <div className={styles.findPassword} onClick={onPasswordForgotClick}>
            {t('login.forgotPassword')}
          </div>
        </>
      )}
    </section>
  );
}
